import { line, readingWindow, type ReadingWindow } from "./shared";
import { formatWorldDate } from "../../journal/journal";
import { touchCloseHint } from "../../input/touch";
import {
  BAND_DEAR,
  BAND_FAMINE,
  BAND_PLENTY,
  BAND_STEADY,
  type EventId,
  type FigureId,
  type JobPost,
  type MarketRow,
  type SettlementId,
  type TownLot,
} from "../../world/types";

/*
 * The market board, read where it stands (chunk U-0's split of `panels.ts`;
 * chunk U-2's move into the reading window and its work table).
 *
 * A snapshot of a query rather than a subscription: prices are derived and
 * never stored, so the board is filled at the moment the player walks up to it.
 * There is no money in this world and there is no per-item price — the number
 * is an index, comparable only against the same settlement last month.
 */

/**
 * How many work orders the board draws before it says how many did not fit: 8.
 *
 * **PROVISIONAL, and a client number rather than a simulation one.** The
 * simulation's own bound is `JOBS_PER_KIND × the nine kinds`, which is
 * fifty-four (`sim_core::jobs`' `MAX_JOBS`) — far more than a page holds, and
 * the page **cannot be scrolled**: the board is read with the pointer locked, so
 * there is no wheel and a row past the bottom is a row nobody can reach (the
 * eras' box carries the same warning). Eight is what stands under two price
 * slips at the height the scale is calibrated to, and nothing in the simulation
 * has an opinion about it. The honest fix is a page that measures itself; until
 * one exists this is a number a playtest moves.
 */
const WORK_SHOWN = 8;

/**
 * One line of the board's work table. See {@link boardWork}.
 *
 * **Three columns and every word in two of them is the simulation's.** The first
 * is `JobKind`'s own noun and the second is `LotKind`'s; the third is chrome
 * about the board — *who has this one* — which is the same kind of sentence as
 * *"no reason posted"* below and the same one the debug window's Places list
 * writes (`dev/debuglog.ts`).
 */
export interface WorkRow {
  /**
   * What kind of work, in the simulation's word: *mine*, *fell*, *forge*.
   *
   * {@link WitnessSource.jobWord}, verbatim. **Not the client's gerund**: the
   * debug window says *felling* because a debug row is a sentence about a body,
   * and copying that vocabulary onto a player surface would be the client
   * keeping a second table of words for work.
   */
  readonly work: string;

  /**
   * The building the work is done in, as the simulation names it — *smithy*,
   * *library* — or `""` for work done to the ground.
   *
   * **A site is named, never arrowed** (the map policy, plan decision D12), and
   * this is as far as *named* goes today: a bench belongs to a lot and a lot has
   * a kind with a word, but a seam of rock and a tree are tiles and the
   * simulation has no name for either. The empty string is that, honestly, and
   * it is filed rather than filled with a compass bearing.
   */
  readonly site: string;

  /**
   * Who has taken it, by the name the street knows them by, or `""` while it is
   * still open.
   *
   * The handle where the roster has no name, on the debug window's terms: an
   * order taken by somebody this town has not named is still a taken order.
   */
  readonly takenBy: string;
}

/**
 * The two vocabularies and the one list {@link boardWork} needs of the wall.
 *
 * Narrower than {@link WitnessSource} on purpose: a panel module that imported
 * the whole boundary would be a panel that could reach for anything, and this
 * one may reach for exactly three answers. The live source and the fixture both
 * satisfy it structurally, so the caller passes its source and nothing adapts.
 */
export interface WorkSource {
  jobWord(kind: number): string;
  lotName(kind: number): string;
  lots(settlement: SettlementId): readonly TownLot[];
}

/**
 * Turns a town's board into the rows the market panel draws under its prices.
 *
 * **Legibility gap (1)** — *what work does this town have going* — answered on
 * the surface a player already walks up to, out of the read the debug window has
 * used since chunk D3 ({@link WitnessSource.jobs}) and two of the simulation's
 * own word tables. Nothing here composes a sentence: it picks nouns and joins
 * them to a site, which is the composing rule's line (`CLAUDE.md`).
 *
 * **A join and not a read.** The board is already held by whoever is drawing the
 * board, the lots are a settlement's plan, and the pairing is by *tile* — a
 * workshop's `work` column is the same tile the marketplace posts its orders at
 * (`world/types.ts`, {@link TownLot.work}), which is what makes the offer the
 * client draws and the command it sends one row.
 *
 * **Open orders first, then taken ones, each in the simulation's own order.**
 * The list is capped ({@link WORK_SHOWN}) because the page cannot scroll, and a
 * cap that showed six taken orders and no open one would answer a question
 * nobody walked up to ask. Inside each group the order is the world hash's own,
 * which is where a sort belongs when the client has no better key — this panel
 * has no player tile, so it cannot sort by distance the way the debug window
 * does.
 *
 * This is a free function rather than a method for the reason the panel takes
 * `posted` as a closure: a panel renders strings and does not hold a simulation.
 * Everything it needs arrives as an argument.
 */
export function boardWork(
  jobs: readonly JobPost[],
  source: WorkSource,
  settlement: SettlementId,
  nameOf: (worker: FigureId) => string,
): readonly WorkRow[] {
  const benches = new Map<string, string>();

  for (const lot of source.lots(settlement)) {
    benches.set(`${lot.work.x},${lot.work.y}`, source.lotName(lot.kind));
  }

  const rows = jobs.map((post) => ({
    work: source.jobWord(post.kind),
    site: benches.get(`${post.site.x},${post.site.y}`) ?? "",
    takenBy: post.worker === null ? "" : nameOf(post.worker),
  }));

  return [...rows.filter((row) => row.takenBy === ""), ...rows.filter((row) => row.takenBy !== "")];
}

/**
 * The market board, read as a panel.
 *
 * Prices are derived and never stored (plan decision 7), so this is a snapshot
 * of a query rather than a subscription: it is filled at the moment the player
 * reads the board and it does not update behind their back. The row that
 * matters is the shock line — a price that moved is weather, a price that moved
 * *because of a record* is news, and the `cause: Option<EventId>` link is what
 * makes the board one of the phase's three channels.
 *
 * **Every line here is the board's own, and that is wave 4 of the voice
 * overhaul.** The cause used to be `WitnessSource.describe` behind an `after #N`
 * tag, so a player who walked up to a board in a town read the developer's
 * chronicle: handle numbers, a casualty subtraction, a parenthetical date. The
 * cause is {@link WitnessSource.notice} now, and the record tags came off the
 * rows with it — a panel that mixed a notice with a handle would be two
 * registers on one board. The ids are still one keypress away in the trace view,
 * which is where a debug affordance belongs.
 *
 * **The centred reading window since chunk U-2**, at the lab's narrow measure,
 * with each commodity a slip pinned under a gold tack: a board is a physical
 * thing in a town square and it reads as one. No `backdrop-filter` (plan
 * decision D6): the board is read with the pointer locked, blur is allowed only
 * where the pointer is free, and what pulls the eye here is a veil tint.
 *
 * **And what the town has going**, under the prices — the work section chunk
 * U-2 added ({@link boardWork}). A board that priced a town's grain and said
 * nothing about the six people it wants out gathering was the half of a market
 * board that is not a market.
 *
 * **There is no claim button, and that is a refusal with a reason** (legibility
 * gap 3, verified against the command rather than assumed). The door exists and
 * the board could name it: taking work is `Command::Gather { pos }` outdoors and
 * `Command::Craft { pos, recipe }` at a bench, both naming the order's own
 * **site tile** — plan decision O5's *a command cites the work, never the board
 * row* — so a button here would be one call to {@link WorldSource.gather}. What
 * refuses it is the other half of that door: `jobs::claim` carries **no distance
 * score at all**, deliberately, because the simulation has never known where the
 * player is standing, and *"the work then runs on the same countdown, and the
 * one thing it skips is the walk: a worker with no body is already where the
 * work is, because the host walked them there"*. The client is the only place
 * that walk is paid, which is why gathering is an aimed verb. A button on a
 * board in the square would fell a tree on the other side of town without
 * anybody going to it. Filed with the command's shape: the two honest ways in
 * are a claim the simulation scores in tiles like an NPC's bid, or a *claimed
 * but not begun* state a body has to reach.
 */
export class MarketPanel {
  private readonly frame: ReadingWindow;

  /**
   * Where the work table is drawn, kept out of the body's own children.
   *
   * Its own element rather than rows appended after the slips, because the two
   * halves are refilled by one call and a section that had to be found and
   * removed first is a section that will one day be found twice.
   */
  private readonly workElement: HTMLElement;

  constructor(root: HTMLElement, touch = false) {
    this.frame = readingWindow({ surface: "market", narrow: true, title: "hud__panel-title" });
    this.frame.hint.textContent = touch ? touchCloseHint("interact") : "F closes";

    this.workElement = document.createElement("div");
    this.workElement.className = "hud__market-work";
    this.workElement.hidden = true;
    this.frame.body.append(this.workElement);

    root.append(this.frame.element);
  }

  get open(): boolean {
    return !this.frame.element.hidden;
  }

  /**
   * Fills the panel from a market read and shows it.
   *
   * `posted` turns an `EventId` into what the board says about it
   * (`WitnessSource.notice`) and is handed in rather than looked up, because a
   * panel renders strings and does not hold a simulation. It is used for one
   * thing only: the *cause* of a shock, on the row's third line, which is the
   * news the board actually carries (`world/types.ts`, `MarketShock.cause`).
   *
   * The closure is what keeps this panel out of a later change. `notice` learned
   * that a board speaks with the ground under it — the same record posts "this
   * town was stormed" here and names the place one town over — and none of that
   * reached this file, because the caller already knew which town it was showing
   * and bound it in (`witness/witness.ts`). A panel that had taken the settlement
   * instead would now hold a handle it never uses.
   *
   * `work` is {@link boardWork}'s answer and **defaults to nothing**, which is
   * the seam chunk U-2 left rather than crossed: composing it needs the town's
   * handle and the roster's names, both of which live in `witness/witness.ts` —
   * the one file fenced out of every chunk of this wave (plan §3, invariant 10).
   * A caller that passes nothing gets the board exactly as it was.
   */
  show(
    title: string,
    rows: readonly MarketRow[],
    posted: (event: EventId) => string,
    work: readonly WorkRow[] = [],
  ): void {
    // Never split and never transformed: it carries the town's name
    // (`ui/panel.css`'s note on the panel title, and the two bugs behind it).
    this.frame.title.textContent = title;

    const slips =
      rows.length === 0 && work.length === 0
        ? // "the board is bare" only when there is nothing at all on it. A town
          // whose prices have not moved but whose people have work to do has a
          // board that is anything but bare, and saying so would be the panel
          // contradicting the section under it.
          [line("the board is bare")]
        : rows.map((row) => this.renderRow(row, posted));

    this.frame.body.replaceChildren(...slips, this.workElement);
    this.renderWork(work);
    this.frame.element.hidden = false;
    // A board opened at the last board's scroll position would be a page that
    // starts halfway down somebody else's town ({@link BookPanel}'s rule).
    this.frame.body.scrollTop = 0;
  }

  hide(): void {
    this.frame.element.hidden = true;
  }

  /**
   * One commodity: what it costs, what band that puts it in, and what moved it.
   *
   * Three lines at most, and they are three because they answer three questions
   * a reader asks in this order — what it costs, when that changed, and why. All
   * three are the board's own words now (class documentation); the last two only
   * appear when the read has them, because a price that has not moved says
   * nothing and a swing nobody can explain does not get an explanation invented
   * for it.
   *
   * **The index is the simulation's number and is never re-derived here.**
   * `PRICE_BASE / (1 + months in store)` is the formula and it is
   * PROVISIONAL sim-side; a client that recomputed it would be a second opinion
   * on a scale that is only comparable against itself (`world/types.ts`,
   * {@link MarketRow.priceIndex}).
   */
  private renderRow(row: MarketRow, posted: (event: EventId) => string): HTMLElement {
    const element = document.createElement("div");
    element.className = "hud__market-row";

    // The tack the slip hangs from: the language's diamond, spent on the only
    // ornament a notice gets.
    const tack = document.createElement("span");
    tack.className = "hud__market-tack objective-diamond";
    tack.setAttribute("aria-hidden", "true");

    const heading = document.createElement("div");
    heading.className = "hud__market-head";

    const commodity = document.createElement("span");
    commodity.className = "hud__market-commodity";
    commodity.textContent = row.commodityName;

    // The number and the word under it. A `<b>` rather than a class on the box,
    // because what is emphasised is the figure and not the column.
    const index = document.createElement("span");
    index.className = "hud__market-index";

    const figure = document.createElement("b");
    figure.textContent = String(row.priceIndex);

    const scale = document.createElement("span");
    scale.textContent = "index";
    index.append(figure, scale);

    const band = document.createElement("span");
    band.className = `hud__market-band status-word ${bandTone(row.band)}`;
    band.textContent = row.bandName;

    heading.append(commodity, index, band);
    element.append(tack, heading);

    if (row.shock === null) {
      return element;
    }

    const when = formatWorldDate(row.shock.when);
    const shock = document.createElement("div");
    shock.className = "hud__market-shock";
    shock.textContent = `${row.commodityName} ${row.shock.moveName}, ${when}`;
    element.append(shock);

    // The notice, bare: the projection composes a whole dated sentence, so a
    // connective in front of it ("after …") would be the panel writing the first
    // word of the board's own line.
    const notice = row.shock.cause === null ? "" : posted(row.shock.cause);
    const cause = document.createElement("div");
    // "no reason posted" rather than nothing at all: the *absence* of a cause is
    // information — it is what tells a price that moved because of the war from a
    // price that moved because it rained (plan decision 7). It is chrome about
    // the board rather than a claim about the world, which is the line a panel is
    // allowed to write for itself — so it wears a modifier and drops out of the
    // serif the board's own sentence is set in.
    cause.className = notice === "" ? "hud__market-cause hud__market-cause--none" : "hud__market-cause";
    cause.textContent = notice === "" ? "no reason posted" : notice;
    element.append(cause);

    return element;
  }

  /**
   * The work the town wants done, or nothing at all.
   *
   * **Nothing at all when the board has no orders**, rather than a heading
   * saying zero — the debug window's own rule for the same list, and the reason
   * is the same one line further out: a header that appeared on every board in
   * the world to say a town wants nothing is a line that costs a reader
   * something every time and tells them nothing once.
   *
   * The rows are a *table of nouns*: the work, the site, and who has it. There
   * is no sentence here and there is deliberately no button — see the class
   * documentation for the claim door, which was found and refused rather than
   * built.
   */
  private renderWork(work: readonly WorkRow[]): void {
    this.workElement.hidden = work.length === 0;

    if (work.length === 0) {
      this.workElement.replaceChildren();

      return;
    }

    const label = document.createElement("div");
    label.className = "hud__market-work-label section-label";
    label.textContent = "work the town wants done";

    const drawn = work.slice(0, WORK_SHOWN).map((row) => this.renderWorkRow(row));

    if (work.length > WORK_SHOWN) {
      // A list that stopped without saying so answers "there is no more work" to
      // a player standing in front of a town that has plenty (`dev/debuglog.ts`'s
      // `furtherOff`, the same sentence for the same reason).
      const more = document.createElement("div");
      more.className = "hud__market-work-more";
      more.textContent = `and ${work.length - WORK_SHOWN} more on the board`;
      drawn.push(more);
    }

    this.workElement.replaceChildren(label, ...drawn);
  }

  /** One order: the work, where it is done, and whose it is. */
  private renderWorkRow(row: WorkRow): HTMLElement {
    const element = document.createElement("div");
    element.className = "hud__market-order";

    const work = document.createElement("span");
    work.className = "hud__market-work-word";
    work.textContent = row.work;

    // "at the smithy", and nothing at all for work done to the ground: the
    // preposition and the article are the only two words on this row the client
    // writes, and they are the join rather than a claim — the noun either side
    // of them is the simulation's.
    const site = document.createElement("span");
    site.className = "hud__market-work-site";
    site.textContent = row.site === "" ? "" : `at the ${row.site}`;

    // Never a `.status-word`, though it is one word wide: that class uppercases,
    // and this cell carries a person's name half the time (invariant 4, and the
    // kit's own note — a status word is never a name).
    const taken = document.createElement("span");
    taken.className = "hud__market-work-taken";
    taken.textContent = row.takenBy === "" ? "open" : `taken by ${row.takenBy}`;

    element.append(work, site, taken);

    return element;
  }
}

/**
 * Which of the kit's four tones a stock band wears.
 *
 * **A rendering switch on `Commodity`'s sibling id**, which is the one thing
 * `world/types.ts` says a client may key off ({@link MarketRow.band}): the
 * *words* are the simulation's (`StockBand::name` — famine, dear, steady,
 * plenty, and there is no fifth) and the colours are the kit's. The ids are
 * checked against those four words at startup like every other vocabulary this
 * client switches on (`world/wasm.ts`'s `checkLocalTier`).
 *
 * A band a later build minted falls to the neutral tone rather than to a colour
 * that would be a guess about whether it is good news.
 */
function bandTone(band: number): string {
  switch (band) {
    case BAND_FAMINE:
      return "status-word--bad";
    case BAND_DEAR:
      return "status-word--poor";
    case BAND_STEADY:
      return "status-word--even";
    case BAND_PLENTY:
      return "status-word--good";

    default:
      return "status-word--even";
  }
}
